import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Library UI state'i.
 * Filtreler (görünüm, tip, klasör, sekme, açık asset) URL'de tutulur — bkz. use-library-filters.
 * Burada sadece URL'e yazılmaması gereken geçici UI state'i var.
 */
interface LibraryStore {
  // Seçim
  selectedIds: string[];
  toggleSelected: (id: string) => void;
  selectMany: (ids: string[]) => void;
  deselectMany: (ids: string[]) => void;
  clearSelection: () => void;

  // Arama
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Grid kart boyutu (px, kalıcı)
  cardSize: number;
  setCardSize: (size: number) => void;

  // Klasör oluşturma dialog'u — oluşturulunca pendingItemIds klasöre eklenir
  isCreateFolderOpen: boolean;
  pendingFolderItemIds: string[];
  openCreateFolder: (pendingItemIds?: string[]) => void;
  closeCreateFolder: () => void;

  // Klasör yeniden adlandırma dialog'u
  renamingFolder: { id: string; name: string } | null;
  openRenameFolder: (folder: { id: string; name: string }) => void;
  closeRenameFolder: () => void;

  // Detay oynatıcısı tercihleri (kalıcı)
  playerVolume: number;
  playerMuted: boolean;
  playerRate: number;
  setPlayerVolume: (volume: number) => void;
  setPlayerMuted: (muted: boolean) => void;
  setPlayerRate: (rate: number) => void;

  // Detaydaki yüzen aksiyon barı — gizli durumu kalıcı, konumu oturumluk
  isFloatingActionsHidden: boolean;
  setFloatingActionsHidden: (hidden: boolean) => void;
  floatingActionsOffset: { x: number; y: number };
  setFloatingActionsOffset: (offset: { x: number; y: number }) => void;

  // Video kare şeridi (Extend)
  isFilmstripOpen: boolean;
  setFilmstripOpen: (open: boolean) => void;

  // Detayda oynayan video — arka plan ambiyansı karelerini buradan okur
  activeVideoEl: HTMLVideoElement | null;
  setActiveVideoEl: (video: HTMLVideoElement | null) => void;
}

export const PLAYER_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export const LIBRARY_CARD_SIZE = { min: 140, max: 360, default: 220 } as const;

export const useLibraryStore = create<LibraryStore>()(
  persist(
    (set) => ({
      selectedIds: [],
      toggleSelected: (id) =>
        set((state) => ({
          selectedIds: state.selectedIds.includes(id)
            ? state.selectedIds.filter((selectedId) => selectedId !== id)
            : [...state.selectedIds, id],
        })),
      selectMany: (ids) =>
        set((state) => ({
          selectedIds: Array.from(new Set([...state.selectedIds, ...ids])),
        })),
      deselectMany: (ids) =>
        set((state) => ({
          selectedIds: state.selectedIds.filter((id) => !ids.includes(id)),
        })),
      clearSelection: () => set({ selectedIds: [] }),

      searchTerm: "",
      setSearchTerm: (searchTerm) => set({ searchTerm }),

      cardSize: LIBRARY_CARD_SIZE.default,
      setCardSize: (cardSize) => set({ cardSize }),

      isCreateFolderOpen: false,
      pendingFolderItemIds: [],
      openCreateFolder: (pendingItemIds = []) =>
        set({ isCreateFolderOpen: true, pendingFolderItemIds: pendingItemIds }),
      closeCreateFolder: () =>
        set({ isCreateFolderOpen: false, pendingFolderItemIds: [] }),

      renamingFolder: null,
      openRenameFolder: (renamingFolder) => set({ renamingFolder }),
      closeRenameFolder: () => set({ renamingFolder: null }),

      // Tarayıcı autoplay politikası sesli oynatmayı engelleyebildiği için ilk açılış sessiz
      playerVolume: 1,
      playerMuted: true,
      playerRate: 1,
      setPlayerVolume: (playerVolume) => set({ playerVolume }),
      setPlayerMuted: (playerMuted) => set({ playerMuted }),
      setPlayerRate: (playerRate) => set({ playerRate }),

      isFloatingActionsHidden: false,
      setFloatingActionsHidden: (isFloatingActionsHidden) => set({ isFloatingActionsHidden }),
      floatingActionsOffset: { x: 0, y: 0 },
      setFloatingActionsOffset: (floatingActionsOffset) => set({ floatingActionsOffset }),

      isFilmstripOpen: false,
      setFilmstripOpen: (isFilmstripOpen) => set({ isFilmstripOpen }),

      activeVideoEl: null,
      setActiveVideoEl: (activeVideoEl) => set({ activeVideoEl }),
    }),
    {
      name: "library-ui-storage",
      partialize: (state) => ({
        cardSize: state.cardSize,
        playerVolume: state.playerVolume,
        playerMuted: state.playerMuted,
        playerRate: state.playerRate,
        isFloatingActionsHidden: state.isFloatingActionsHidden,
      }),
    }
  )
);
